import {
  CACHE_MANAGER,
  getDragOffset,
  getTouchDistance,
  createButton,
  isIphone,
} from "../utils.js";
import Layer from "./Layer.js";
import ControlsLayer from "./ControlsLayer.js";
import CarouselLayer from "./CarouselLayer.js";
import ZoomHandler from "./ZoomHandler.js";
import BoundingBoxHandler from "./BoundingBoxHandler.js";
import HotspotsManager from "./HotspotsManager.js";
import CanvasLayer from "./CanvasLayer.js";
import ResourceManager from "./ResourceManager.js";
import YouTubeLayer from "./YouTubeLayer.js";
import InteriorLayer from "./InteriorLayer.js";
import {
  RESOLUTION_480P,
  RESOLUTION_720P,
  RESOLUTION_1080P,
  VIEWER_MODE_PORTRAIT,
  VIEWER_MODE_LANDSCAPE,
  VIEWER_MODE_BOUNDING_BOX,
} from "../consts.js";

import Ajv2019 from "ajv/dist/2019.js";
import * as spinnerDataSchema from "../spinner.schema.json";
const ajv = new Ajv2019();
const validateData = ajv.compile(spinnerDataSchema);

export const SPINNABLE_LAYERS = ["default", "doors", "lights", "showcase"];

const INSPECTOR_MODE_CLASSNAME = "inspector-mode";

const DEFAULT_SPINNING_SPEED = 1;
const DEFAULT_MAX_ZOOM = 6;

// Define UIConfig type in jsdoc
/**
 * @typedef {object} UIConfig
 * @property {number} [spinningSpeed]
 * @property {number} [maxZoom]
 * @property {number|null} [activeColumn]
 * @property {number|null} [activeRow]
 * @property {string} [buttonsPlacement]
 * @property {string} [viewerMode]
 * @property {string} [canScroll]
 * @property {string} [canFullscreen]
 * @property {string} [defaultLayer]
 * @property {boolean} [cleanUi]
 * @property {boolean} [showGallery]
 * @property {boolean} [hideGallery]
 * @property {boolean} [hideDoors]
 * @property {boolean} [hideLights]
 * @property {boolean} [hideShowcase]
 * @property {boolean} [hideInterior]
 * @property {boolean} [hideZoom]
 * @property {boolean} [hideHotspots]
 */

/**
 *
 * @param {HTMLElement|ShadowRoot} root
 */

/**
 * @param {*} root
 */
class Spinner {
  constructor(root) {
    this.root = root;
    this.uiX = 0;
    this.uiY = 0;
    this.sceneWidth = 0;
    this.sceneHeight = 0;
    this.elById = (id) => {
      return root.getElementById(id);
    };

    this.spinnerNode = this.elById("spinner");
    this.sceneNode = this.elById("scene");
    this.leadImageNode = this.elById("lead-image");
    this.contextMenuNode = this.elById("context-menu");
    this.canvasLayerNode = this.elById("canvas-layer");
    this.zoomLayerNode = this.elById("zoom-layer");
    this.interiorLayerNode = this.elById("interior-layer");
    this.hotspotsLayerNode = this.elById("hotspots-layer");
    this.controlsLayerNode = this.elById("controls-layer");
    this.carouselLayerNode = this.elById("carousel-layer");
    this.youtubeLayerNode = this.elById("youtube-layer");
    this.loaderLayerNode = this.elById("loader-layer");
    this.brandNode = this.elById("brand");

    this.canvasLayer = new CanvasLayer(this, this.canvasLayerNode);
    this.zoomLayer = new Layer(this, this.zoomLayerNode);
    this.interiorLayer = new InteriorLayer(this, this.interiorLayerNode);
    /**
     *
     * @type {Layer}
     */
    this.hotspotsLayer = new Layer(this, this.hotspotsLayerNode);
    this.controlsLayer = new ControlsLayer(this, this.controlsLayerNode);
    this.carouselLayer = new CarouselLayer(this, this.carouselLayerNode);
    this.youTubeLayer = new YouTubeLayer(this, this.youtubeLayerNode);
    this.loaderLayer = new Layer(this, this.loaderLayerNode);

    this.resourceManager = new ResourceManager();

    this.resourceManager.onHighQualityImageReady((image) => {
      if (this.activeImage !== image) {
        return;
      }

      this.renderScene();
    });

    this.resourceManager.onMaxQualityImageReady((image) => {
      /*
       * It does not make sense to show the max quality image if there is
       * no effective zoom.
       * This can happen when the user scrolls back and forth quickly and the
       * max quality image loads during a zoomed-out state with no effective
       * zoom.
       */
      if (this.zoomScale === 1 || this.activeImage !== image) {
        return;
      }

      if (this.zoomLayer.subject.src === image.maxQualityImage.blob) {
        return;
      }

      this.zoomLayer.subject.src = image.maxQualityImage.blob;
      this.renderScene();
      this.dispatchEvent(
        new CustomEvent("frameupscale", {
          composed: true,
          detail: {
            image: image,
          },
        }),
      );
    });

    this.dragEvent = null;
    this.startTouchEvent = null;
    this.spinningStarted = false;
    this.zoomScale = 1;
    this.contentWidth = 0;
    this.contentHeight = 0;
    this.transformOriginCSS = "0 0 0";
    this.cleanUi = false;
    this.initialImageSize =
      this.spinnerNode.clientWidth <= RESOLUTION_480P
        ? RESOLUTION_480P
        : RESOLUTION_720P;
    this.supportsScroll = true;
    this.supportsFullscreen = true;
    this.defaultLayer = null;
    this.activeLayer = null;
    this.activeRow = 0;
    this.activeColumn = 0;
    this.activeImage = null;
    this.spinningSpeed = DEFAULT_SPINNING_SPEED;
    this.maxZoom = DEFAULT_MAX_ZOOM;
    this.defaultViewerMode = VIEWER_MODE_PORTRAIT;
    this.hotspotsActive = true;
    this.leadImageBoundingRect = null;
    this.spinnerBoundingRect = null;
    this._viewerMode = null;
    this.zoomHandler = new ZoomHandler(this);
    this.hotspotsManager = new HotspotsManager(this);

    this.bindEventHandlers();
  }

  bindEventHandlers() {
    this._handlersInstalled = false;
    this._onWheel = this.onWheel.bind(this);
    this._onDragStart = this.onDragStart.bind(this);
    this._onDragEnd = this.onDragEnd.bind(this);
    this._onMouseMove = this.onMouseMove.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onPointerDown = this.onPointerDown.bind(this);
    this._onMouseDown = this.onMouseDown.bind(this);
    this._onContextMenu = this.onContextMenu.bind(this);
    this._onWindowResize = this.onWindowResize.bind(this);
  }

  set inspectorMode(flag) {
    if (flag) {
      if (this.spinnerNode.classList.contains(INSPECTOR_MODE_CLASSNAME)) return;

      this.spinnerNode.classList.add(INSPECTOR_MODE_CLASSNAME);
      const distance = -20;
      const layers = this.spinnerNode.querySelectorAll(".layer");
      layers.forEach((layer, i) => {
        const currentAmount = distance + i * 5;
        layer.oldCssText = layer.style.cssText;
        layer.style.cssText = ` transform: scale(0.69) rotateY(50deg) rotateX(-5deg) rotateZ(-6deg)
          translateZ(${currentAmount}em) translateY(${currentAmount}em);`;
      });
    } else if (this.spinnerNode.classList.contains(INSPECTOR_MODE_CLASSNAME)) {
      this.spinnerNode.classList.remove(INSPECTOR_MODE_CLASSNAME);
      const layers = this.spinnerNode.querySelectorAll(".layer");
      layers.forEach((layer) => {
        layer.style.cssText = layer.oldCssText;
      });
    }
  }

  get zoomPointX() {
    return this.zoomHandler.zoomPointX;
  }

  set zoomPointX(value) {
    this.zoomHandler.zoomPointX = value;
  }

  get zoomPointY() {
    return this.zoomHandler.zoomPointY;
  }

  set zoomPointY(value) {
    this.zoomHandler.zoomPointY = value;
  }

  set notFound(value) {
    if (value) {
      this.spinnerNode.classList.add("not-found");
    } else {
      this.spinnerNode.classList.remove("not-found");
    }
  }

  get notFound() {
    return this.spinnerNode.classList.contains("not-found");
  }

  get smoothTransform() {
    return this.zoomLayer.smoothTransform;
  }

  set smoothTransform(value) {
    this.zoomLayer.smoothTransform = value;
    this.hotspotsLayer.smoothTransform = value;
  }

  set uiControlsEnabled(value) {
    this.controlsLayer.disabled = !value;
  }

  set uiHotspotsEnabled(value) {
    this.hotspotsLayer.disabled = !value;
  }

  get uiHotspotsEnabled() {
    return this.hotspotsLayer.disabled;
  }

  set uiContextMenuVisibility(value) {
    if (value) {
      this.contextMenuNode.style.display = null;
      const appVersionNode = this.spinnerNode.querySelector("#app-version");

      setTimeout(() => {
        try {
          navigator.clipboard
            .write([
              new ClipboardItem({
                "text/plain": appVersionNode.innerText,
              }),
            ])
            .catch(console.error);
        } catch (_e) {
          const textArea = document.createElement("textarea");
          textArea.value = appVersionNode.innerText;

          // Avoid scrolling to bottom
          textArea.style.position = "fixed";
          textArea.style.top = "0";
          textArea.style.left = "0";
          textArea.style.opacity = "0";

          this.spinnerNode.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            // eslint-disable-next-line etc/no-deprecated
            const successful = document.execCommand("copy");
            if (successful) {
              console.log("Fallback: Text copied to clipboard");
            } else {
              console.error("Fallback: Unable to copy");
            }
          } catch (err) {
            console.error("Fallback: Oops, unable to copy", err);
          }

          this.spinnerNode.removeChild(textArea);
        }
      }, 50);
    } else {
      this.contextMenuNode.style.display = "none";
    }
  }

  set buttonsPlacement(value) {
    this.controlsLayer.host.className = `layer layer-controls ${value}`;
  }

  /**
   *
   * @param {string|null} value
   */
  set viewerMode(value) {
    this._viewerMode = value;
    this.spinnerNode.classList.remove(
      VIEWER_MODE_PORTRAIT,
      VIEWER_MODE_LANDSCAPE,
      VIEWER_MODE_BOUNDING_BOX,
    );

    if (value) {
      this.spinnerNode.classList.add(value);
    }
  }

  get viewerMode() {
    return this._viewerMode;
  }

  set uiFullscreenEnabled(value) {
    const fullscreenElement =
      document.fullscreenElement ?? document.webkitFullscreenElement;
    if (value && !fullscreenElement) {
      this.spinnerNode.requestFullscreen();
      this.spinnerNode.classList.add("fullscreen");
    } else if (!value && fullscreenElement) {
      const exitFullscreen =
        "exitFullscreen" in document
          ? document.exitFullscreen.bind(document)
          : document.webkitExitFullscreen.bind(document);
      exitFullscreen().catch(console.error);
      this.spinnerNode.classList.remove("fullscreen");
    }
  }

  get uiFullscreenEnabled() {
    return Boolean(
      document.fullscreenElement ?? document.webkitFullscreenElement,
    );
  }

  get interiorViewerEnabled() {
    return this.interiorLayer.enabled;
  }
  set interiorViewerEnabled(value) {
    this.interiorLayer.enabled = value;
  }

  get interiorViewer() {
    return this.interiorLayer.viewer;
  }

  on(event, handler, options) {
    this.spinnerNode.addEventListener(event, handler, options);
  }

  off(event, handler, options) {
    if (!handler) return;

    this.spinnerNode.removeEventListener(event, handler, options);
  }

  /**
   *
   * @param {CustomEvent} event
   */
  dispatchEvent(event) {
    this.spinnerNode.dispatchEvent(event);
  }

  /**
   *
   * @param {string} layerType
   * @returns {boolean}
   */
  canActivateLayer(layerType) {
    switch (layerType) {
      case "doors":
        return !this.hideDoors;
      case "lights":
        return !this.hideLights;
      case "showcase":
        return !this.hideShowcase;
      case "interior":
        return !this.hideInterior;
      default:
        return true;
    }
  }

  /**
   *
   * @param {string} layerType
   */
  async setActiveLayer(layerType) {
    let chosenLayer = this.layers[layerType];
    if (!chosenLayer?.type) {
      throw new Error(
        `Selected layer is missing type. Expected type: ${layerType}`,
      );
    }

    if (!this.canActivateLayer(chosenLayer.type)) {
      if (!this.canActivateLayer(this.layers[this.defaultLayerType].type)) {
        throw new Error(
          `Layer "${layerType}" is not available and ` +
            `fallback layer "${this.defaultLayerType}" is not available either`,
        );
      }

      chosenLayer = this.layers[this.defaultLayerType];
    }

    this.controlsLayer.setActiveLayerButton(chosenLayer.type);
    if (this.activeLayer === chosenLayer) {
      return;
    }

    const oldLayer = this.activeLayer;
    this.activeLayer = chosenLayer;

    await this.setLeadImage();

    const activeLayerIsInterior = this.activeLayer.type === "interior";

    this.interiorViewerEnabled = activeLayerIsInterior;
    this.hotspotsLayer.visibility = !activeLayerIsInterior;

    if (!activeLayerIsInterior) {
      await this.loadLayerContent();

      if (this.hotspotsActive && !this.cleanUi) {
        this.hotspotsLayer.visibility = true;
      }
    }

    this.dispatchEvent(
      new CustomEvent("layerchange", {
        composed: true,
        detail: {
          layer: chosenLayer,
          oldLayer: oldLayer,
        },
      }),
    );
  }

  /**
   *
   * @param {string} id
   * @param {Function} action
   * @param {boolean} [appendImmediately]
   * @returns {HTMLButtonElement}
   */
  addActionButton(id, action, appendImmediately = true) {
    const btn = createButton(id);

    btn.onpointerup = (event) => {
      if (event.button === 2) return;

      action.call(this, event);
    };

    if (appendImmediately) {
      this.controlsLayer.appendTo(btn, "buttons");
    }

    return btn;
  }

  /**
   *
   * @param {UIConfig} uiConfig
   */
  applyUiConfig(uiConfig) {
    this.spinningSpeed = uiConfig.spinningSpeed ? uiConfig.spinningSpeed : 1;
    this.maxZoom = uiConfig.maxZoom ? uiConfig.maxZoom : 6;
    this.buttonsPlacement = uiConfig.buttonsPlacement ?? "bottom";
    this.supportsScroll = uiConfig.canScroll
      ? uiConfig.canScroll === "true"
      : true;
    this.supportsFullscreen = uiConfig.canFullscreen
      ? uiConfig.canFullscreen === "true"
      : true;
    this.defaultLayerType = uiConfig.defaultLayer ?? this.defaultLayerType;
    this.cleanUi = uiConfig.cleanUi ?? false;
    this.showGallery = uiConfig.showGallery ?? false;
    this.hideGallery = uiConfig.hideGallery ?? false;
    this.hideDoors = uiConfig.hideDoors ?? false;
    this.hideLights = uiConfig.hideLights ?? false;
    this.hideShowcase = uiConfig.hideShowcase ?? false;
    this.hideInterior = uiConfig.hideInterior ?? false;
    this.hideZoom = uiConfig.hideZoom ?? false;
    this.hideHotspots = uiConfig.hideHotspots ?? false;

    const singleBoundingBox = this.boundingBox ? [this.boundingBox] : null;

    this.boundingBoxHandler = new BoundingBoxHandler(
      this,
      this.boundingBoxes || singleBoundingBox,
    );

    this.activeColumn = uiConfig.activeColumn ?? 0;
    this.activeRow = uiConfig.activeRow ?? 0;

    this.populateDefaults();
    this.applyViewerMode(uiConfig.viewerMode);
    this.buildUserInterface();
  }

  /**
   *
   * @param {string} hash
   * @param {UIConfig} [uiConfig]
   * @returns {Promise<void>}
   */
  async setHash(hash, uiConfig) {
    if (!hash) {
      return;
    }

    this.loaderLayer.visibility = true;
    const response = await fetch(
      `${import.meta.env.VITE_SPINNER_DATA_URL}${hash}.json`,
    );

    this.notFound = !response.ok;

    const json = await response.json();

    if (validateData(json)) {
      this.setData(json);
      this.applyUiConfig(uiConfig);
    } else {
      console.warn(validateData.errors);
    }

    return json;
  }

  populateDefaults() {
    this.defaultLayer = this.activeLayer || this.layers[this.defaultLayerType];
    if (!this.defaultLayer) {
      throw new Error(`Default layer "${this.defaultLayerType}" not found`);
    }

    this.activeRow = this.activeRow || this.defaultLayer.default_row;
    this.activeColumn = this.activeColumn || this.defaultLayer.default_column;
    this.activeImage = null;
    this.aspectRatioCSS = null;

    if (this.defaultLayer.type === "interior" && this.interiorViewerEnabled) {
      return;
    }

    this.interiorLayer.deactivate();
  }

  setData(data) {
    this.productName = data.product_name;
    this.clientName = data.client_name;
    this.spinnerHash = data.spinner_hash;
    this.defaultLayerType = data.default_layer;
    this.imagesBaseUrl = data.images_base_url;
    this.availableLabels = data.available_labels || [];
    this.defaultViewerMode = data.viewer_mode ?? VIEWER_MODE_PORTRAIT;
    this.intellicrops = data.intellicrops || {};
    this.galleryImages = data.gallery || [];
    this.showBranding = data.show_branding;
    this.layers = data.layers;
    this.hotspots = data.hotspots || {};
    this.hasHotspots =
      Object.keys(this.hotspots).length > 0 &&
      Object.keys(this.intellicrops).length > 0;
    this.placeholder = data.placeholder;
    this.boundingBox = data.bounding_box;
    this.boundingBoxes = data.bounding_boxes || null;
    this.hasInterior = Boolean(data.interior_image_url || data.interior_image);

    if (this.hasInterior) {
      this.interiorLayer.configure({
        image: data.interior_image,
        imageUrl: data.interior_image_url,
        imageMaxResolution: data.interior_image_max_resolution,
        imageWidth: data.interior_image_width,
        isImageTiled: "interior_image_tiles" in data,
      });
      this.layers.interior = this.interiorLayer.createLayerData();
    }

    this.youtubeId = data.youtube_id || null;
    this.availableLayers = Object.keys(this.layers);
    this.activeLayer = null;
    this.activeRow = 0;
    this.activeColumn = 0;

    this.resourceManager.setBaseUrl(this.imagesBaseUrl);
    this.resourceManager.setDefaultSize(this.initialImageSize);

    this.root.dispatchEvent(
      new CustomEvent("datachange", {
        composed: true,
        detail: {
          customerData: {
            productName: this.productName,
            clientName: this.clientName,
          },
        },
      }),
    );
  }

  onPointerDown(event) {
    if (!this.controlsLayer.host.contains(event.target)) {
      this.controlsLayer.subject.classList.remove("expand");
    }
  }

  onMouseDown(event) {
    if (!this.contextMenuNode.contains(event.target) && event.button === 0) {
      this.uiContextMenuVisibility = false;
    }
  }

  onContextMenu(event) {
    event.preventDefault();
    this.contextMenuNode.style.left = `${event.clientX}px`;
    this.contextMenuNode.style.top = `${event.clientY}px`;
    this.uiContextMenuVisibility = true;
    return false;
  }

  installEventHandlers() {
    if (this.supportsScroll) {
      this.root.host.addEventListener("wheel", this._onWheel, false);
      this.root.host.addEventListener(
        "wheel",
        () => {
          this.spinnerNode.classList.add("ready");
        },
        { once: true },
      );
    } else {
      this.root.host.removeEventListener("wheel", this._onWheel, false);
      this.zoomScale = 1;
    }

    /*
     * Event handlers after this point are not reactive and need to be there
     * all the time so we can only install them once
     */
    if (this._handlersInstalled) {
      return;
    }

    window.addEventListener("resize", this._onWindowResize);
    window.addEventListener("scroll", this._onWindowResize);

    this.zoomLayer.on("mousedown", this._onDragStart, false);

    window.addEventListener("mouseleave", this._onDragEnd, false);

    window.addEventListener("mouseup", this._onDragEnd, false);

    this.zoomLayer.on("touchstart", this._onDragStart, false);

    this.zoomLayer.on("touchcancel", this._onDragEnd, false);

    this.zoomLayer.on("touchend", this._onDragEnd, false);

    window.addEventListener("mousemove", this._onMouseMove, false);

    this.zoomLayer.on("touchmove", this._onTouchMove, false);

    this.on("pointerdown", this._onPointerDown);

    this.on("mousedown", this._onMouseDown, false);

    this.on("contextmenu", this._onContextMenu, false);

    this.on(
      "pointerdown",
      () => {
        this.spinnerNode.classList.add("ready");
      },
      { once: true },
    );

    this._handlersInstalled = true;
  }

  buildControls() {
    this.controlsLayer.reset();

    if (!this.hideZoom) {
      this.controlsLayer.buildZoomControl();
    }

    if (this.supportsFullscreen) {
      this.controlsLayer.buildFullscreenControl();
    }

    this.controlsLayer.buildMenuControl();

    for (const layerType of this.availableLayers) {
      if (this.canActivateLayer(layerType)) {
        this.controlsLayer.addLayerButton(layerType);
      }
    }

    if (!this.hideGallery && this.galleryImages.length) {
      this.controlsLayer.addButton("gallery", "popups", () => {
        this.showCarousel(this.galleryImages).catch(console.error);
      });
    }

    if (this.youtubeId) {
      this.controlsLayer.addButton("youtube", "popups", () => {
        this.youTubeLayer.show(this.youtubeId);
      });
    }

    if (this.hideHotspots) {
      this.hotspotsManager.setLabels([]);
    } else if (this.hasHotspots) {
      this.hotspotsManager.setLabels(this.availableLabels);
    }

    /*
     * Hide the menu on iOS (not iPadOS) if there is only one layer
     * Since iOS does not support full screen
     */
    if (
      isIphone() &&
      this.availableLayers.length === 1 &&
      !this.hasInterior &&
      !this.hasHotspots &&
      !this.galleryImages.length
    ) {
      this.controlsLayer.subject.classList.add("no-controls");
    }

    this.brandNode.style.display = this.showBranding ? "" : "none";
  }

  buildUserInterface() {
    this.canvasLayer.init();

    this.sceneNode.style.opacity = 0;
    this.hotspotsLayer.visibility = !this.cleanUi;
    this.controlsLayer.visibility = !this.cleanUi;

    if (this.carouselLayer.isOpen) {
      this.carouselLayer.close();
    }

    if (this.youTubeLayer.isOpen) {
      this.youTubeLayer.close();
    }

    this.interiorLayer.destroyViewer();

    const variant = this.defaultLayer.rows > 1 ? "variant-3d" : "variant-360";
    this.spinnerNode.classList.remove("variant-3d", "variant-360");
    this.spinnerNode.classList.add(variant);
    this.buildControls();
    this.installEventHandlers();

    this.smoothTransform = false;
    this.setActiveLayer(this.activeLayer?.type || this.defaultLayerType)
      .then(() => {
        if (
          this.showGallery &&
          !this.hideGallery &&
          this.galleryImages.length > 0
        ) {
          this.showCarousel(this.galleryImages).catch(console.error);
        }

        /*
         * We need to calculate the layout when spinner
         * loads a layer for the first time
         */
        this.recalculateLayout(() => {
          this.renderScene();

          setTimeout(() => {
            const a = this.sceneNode.animate(
              { opacity: 1 },
              {
                duration: 200,
                fill: "both",
              },
            );

            a.finished.then(() => {
              a.commitStyles();
              this.smoothTransform = true;
            });
          }, 200);
        });
      })
      .catch(console.error);
  }

  onWindowResize() {
    clearTimeout(this.windowResizeThrottle);
    this.windowResizeThrottle = setTimeout(() => {
      if (!this.uiFullscreenEnabled) {
        this.spinnerNode.classList.remove("fullscreen");
      }

      if (this.activeLayer.type === "interior") {
        this.recalculateLayout(() => {
          this.renderScene();
        });
      } else {
        this.loadLayerContent()
          .then(() => {
            this.recalculateLayout(() => {
              this.renderScene();
            });
          })
          .catch(console.error);
      }
    }, 250);
  }

  /**
   *
   * @returns {Promise<void>}
   */
  async loadLayerContent() {
    this.loaderLayer.visibility = true;
    const images = Object.values(this.activeLayer.images);
    const imagesMap = this.activeLayer.images;
    const activeRowImages = [];

    /*
     * When the active row is different, we assume the row has changed by user
     * and the images of that row should be loaded first.
     */
    const row =
      this.activeRow !== this.activeLayer.default_row
        ? this.activeRow
        : this.activeLayer.default_row;
    const startIndex = row - 1;

    // Load the active row
    for (let i = 0; i < this.activeLayer.columns; i++) {
      const image = imagesMap[`${i + 1}-${startIndex + 1}`];
      if (!image) continue;

      const imageRequest = image.url
        ? CACHE_MANAGER.image(image.url)
        : CACHE_MANAGER.image(
            `${this.imagesBaseUrl}/${image.hash}.${this.initialImageSize}`,
          );

      activeRowImages.push(imageRequest);
    }

    await Promise.all(activeRowImages);

    // Load other rows
    for (const image of images) {
      if (image.url) {
        void CACHE_MANAGER.image(image.url);
      } else {
        void CACHE_MANAGER.image(
          `${this.imagesBaseUrl}/${image.hash}.${this.initialImageSize}`,
        );
      }
    }

    this.loaderLayer.visibility = false;
  }

  setSpinnerColumn(column) {
    let value = parseInt(column);

    value = value < 1 ? this.activeLayer.columns : value;
    value = value > this.activeLayer.columns ? 1 : value;
    this.activeColumn = value;

    this.renderScene();
  }

  setSpinnerRow(row) {
    let value = parseInt(row);

    value = Math.max(value, 1);
    value = Math.min(value, this.activeLayer.rows);
    this.activeRow = value;

    this.renderScene();
  }

  disableFeaturesOnSpinning() {
    if (this.spinningStarted) {
      this.smoothTransform = false;
      this.uiControlsEnabled = false;
      this.uiHotspotsEnabled = false;
    }
  }

  onDragStart(event) {
    event.preventDefault();
    if (
      this.carouselLayer.isOpen ||
      this.controlsLayer.host.contains(event.target)
    ) {
      return;
    }

    this.dragEvent = event;
    this.startTouchEvent = event;
    this.spinningStarted = true;
  }

  onMouseMove(event) {
    this.disableFeaturesOnSpinning();
    const fromX = this.dragEvent ? this.dragEvent.clientX : null;
    const fromY = this.dragEvent ? this.dragEvent.clientY : null;
    const tillX = event.clientX;
    const tillY = event.clientY;

    if (this.zoomScale === 1)
      this.handleSpin(event, fromX, fromY, tillX, tillY);
    else if (this.zoomScale > 1) {
      this.handleTransform(event, fromX, fromY, tillX, tillY);
    }
  }

  onTouchMove(event) {
    event.preventDefault();

    this.disableFeaturesOnSpinning();

    if (event.touches.length < 2) this.onTouchMoveDrag(event);
    if (event.touches.length > 1) this.onTouchMovePinch(event);
  }

  /**
   * Handle touch event for dragging.
   * @param {TouchEvent} event
   */
  onTouchMoveDrag(event) {
    const fromX = this.dragEvent ? this.dragEvent.touches[0].clientX : null;
    const fromY = this.dragEvent ? this.dragEvent.touches[0].clientY : null;

    const tillX = event.touches[0].clientX;
    const tillY = event.touches[0].clientY;

    if (this.zoomScale === 1)
      this.handleSpin(event, fromX, fromY, tillX, tillY);
    else if (this.zoomScale > 1)
      this.handleTransform(event, fromX, fromY, tillX, tillY);
  }

  /**
   * Handle touch event for pinching.
   * @param {TouchEvent} event
   */
  onTouchMovePinch(event) {
    const distanceFrom = getTouchDistance(this.startTouchEvent.touches);
    const distanceTill = getTouchDistance(event.touches);

    // Calculate zoom center relative to image
    const [touch0, touch1] = event.touches;
    const clientX = (touch0.clientX + touch1.clientX) / 2;
    const clientY = (touch0.clientY + touch1.clientY) / 2;

    // Calculate new scale based on pinch ratio
    const scaleFactor = distanceTill / distanceFrom;
    let newScale = this.zoomScale * scaleFactor;

    // Enforce scale bounds
    newScale = Math.max(1, Math.min(this.maxZoom, newScale));

    this.startTouchEvent = event;

    this.setZoomScale(clientX, clientY, newScale);
  }

  onDragEnd(event) {
    event.preventDefault();

    this.dragEvent = null;
    this.startTouchEvent = null;
    this.spinningStarted = false;

    this.smoothTransform = true;
    this.uiControlsEnabled = true;

    if (this.zoomScale <= 1.5) {
      this.uiHotspotsEnabled = true;
    }
  }

  handleSpin(event, fromX, fromY, tillX, tillY) {
    const containerX = 1920 / this.sceneWidth;
    const offsetX = fromX
      ? getDragOffset(containerX, fromX, tillX, this.spinningSpeed)
      : 0;

    const containerY = 1080 / this.sceneHeight;
    const offsetY = fromY
      ? getDragOffset(containerY, fromY, tillY, this.spinningSpeed)
      : 0;

    if (offsetX !== 0) this.setSpinnerColumn(this.activeColumn + offsetX);
    if (offsetX !== 0) this.dragEvent = event;

    if (offsetY !== 0) this.setSpinnerRow(this.activeRow - offsetY);
    if (offsetY !== 0) this.dragEvent = event;
  }

  handleTransform(event, fromX, fromY, tillX, tillY) {
    if (fromX === null || fromY === null) return;

    const pointX = (this.zoomPointX || 0) + (tillX - fromX);
    const pointY = (this.zoomPointY || 0) + (tillY - fromY);

    this.setZoomPoint(pointX, pointY);

    this.dragEvent = event;
  }

  onWheel(event) {
    event.preventDefault();
    if (!SPINNABLE_LAYERS.includes(this.activeLayer.type)) {
      return;
    }

    const delta = Math.sign(event.deltaY);
    const scale = Math.round((this.zoomScale - delta / 5) * 100) / 100;

    this.setZoomScale(
      event.clientX - this.uiX,
      event.clientY - this.uiY,
      scale,
    );
  }

  setZoomScale(x, y, scale) {
    const prevScale = this.zoomScale;
    this.zoomScale = parseFloat(
      Math.min(Math.max(scale, 1), this.maxZoom).toFixed(3),
    );

    if (this.zoomScale <= 1.015) this.zoomScale = 1;

    const { pointX, pointY } = this.zoomHandler.getZoomPoint(
      x,
      y,
      this.zoomScale,
      prevScale,
    );

    this.setZoomPoint(pointX, pointY);
  }

  setZoomPoint(pointX, pointY) {
    this.zoomHandler.setZoomPoint(pointX, pointY, this.zoomScale);
    this.renderScene();
  }

  getTransform() {
    const { zoomedWidth, zoomedHeight } = this.zoomHandler;
    const zoomHandlerCss = this.zoomHandler.getAsCss();
    const boundingBoxHandlerCss = this.boundingBoxHandler.getAsCss(
      zoomedWidth,
      zoomedHeight,
    );

    return {
      ...zoomHandlerCss,
      ...boundingBoxHandlerCss,
    };
  }

  applyTransformToHotspotsLayer(transform) {
    let finalTransform = transform;
    // hide hotspots after zoom scale is bigger than 1.5
    if (this.zoomScale > 1.5) {
      this.uiHotspotsEnabled = false;
      finalTransform = { ...transform, opacity: 0 };
    } else if (!this.spinningStarted) {
      this.uiHotspotsEnabled = true;
      finalTransform = { ...transform, opacity: 1 };
    }

    this.hotspotsLayer.animate(finalTransform);
  }

  getDefaultViewDimensions(transforms, margins) {
    const diffY = (margins.top - margins.bottom) / 2;
    const diffX = (margins.left - margins.right) / 2;

    return {
      x: diffX * transforms.width,
      y: diffY * transforms.height,
      width: transforms.width,
      height: transforms.height,
    };
  }

  renderScene(callback) {
    if (!SPINNABLE_LAYERS.includes(this.activeLayer.type)) {
      return;
    }

    cancelAnimationFrame(this.renderSceneThrottle);

    this.renderSceneThrottle = requestAnimationFrame(() => {
      const newImage =
        this.activeLayer.images[`${this.activeColumn}-${this.activeRow}`];

      if (newImage !== this.activeImage) {
        this.dispatchEvent(
          new CustomEvent("framechange", {
            composed: true,
            detail: {
              transformOrigin: this.transformOriginCSS,
              activeColumn: this.activeColumn,
              activeRow: this.activeRow,
              image: newImage,
            },
          }),
        );
      }

      this.activeImage = newImage;

      const transform = this.getTransform();

      this.dispatchEvent(
        new CustomEvent("framemorph", {
          composed: true,
          detail: {
            scale: this.zoomScale,
            transform: transform,
            smoothTransform: this.smoothTransform,
          },
        }),
      );

      this.applyTransformToHotspotsLayer(transform);

      // Apply zoom layer css only when the zoom scale is bigger than one
      if (this.zoomScale > 1.01) {
        this.resourceManager.upscale(this.activeImage);
        this.zoomLayer.subjectOpacity = 1;
        this.zoomLayer.renderFast = true;
        this.zoomLayer.animate(transform, () => {
          this.zoomLayer.renderFast = false;
        });

        return;
      }

      this.renderBoundingBox();

      const zoomHandlerValues = this.zoomHandler.getValues();
      const boundingBoxValues = this.boundingBoxHandler.getValues();
      const viewDimensions = this.getDefaultViewDimensions(
        zoomHandlerValues,
        boundingBoxValues,
      );

      const frame = this.resourceManager.getFrame(this.activeImage);
      this.canvasLayer.draw(frame.bitmap, viewDimensions, callback);

      const layerHotspots = this.hotspots[this.activeLayer.type] || {};
      this.hotspotsManager.render(layerHotspots[this.activeImage.id]);
      this.zoomLayer.animate(transform, () => {
        this.zoomLayer.renderFast = false;
        this.zoomLayer.subjectOpacity = 0;
        this.zoomLayer.subject.src = frame.blob;
      });

      this.root.host.activeColumn = this.activeColumn;
      this.root.host.activeRow = this.activeRow;
    });
  }

  /**
   *
   * @param {object[]} slides
   * @param {DOMRect} [expandFrom] -  Expand animation from here
   * @returns {Promise<void>}
   */
  async showCarousel(slides, expandFrom) {
    try {
      this.loaderLayer.visibility = true;
      this.carouselLayer.ready();
      await this.carouselLayer.setSlides(slides);
      this.loaderLayer.visibility = false;

      if (slides.length > 1) {
        this.carouselLayer.sliderArrowsVisibility = true;
      }

      if (expandFrom) {
        this.carouselLayer.expandFrom(expandFrom);
      } else {
        this.carouselLayer.slideUp();
      }
    } catch (error) {
      this.loaderLayer.visibility = false;
      console.error(error);
    }
  }

  async setLeadImage() {
    const image = this.activeImage || {
      url: this.placeholder,
    };

    if (!this.placeholder && !this.activeImage) {
      throw new Error(
        "Placeholder is required: make sure the data that is provided has a `placeholder` property",
      );
    }

    const poster = await CACHE_MANAGER.image(
      image.url || `${this.imagesBaseUrl}/${image.hash}.${RESOLUTION_1080P}`,
    );

    return this.updateLeadImage(poster.blob);
  }

  async updateLeadImage(poster) {
    /*
     * Fix APP-632 Changing layer in firefox does not cause the images of the
     * new layer to be preloaded. On firefox, decode is not being called
     * again on leadImageNode even after the src change
     */
    const preloadImage = new Image();
    preloadImage.src = poster;

    const spinnerClassList = this.spinnerNode.classList;

    return await preloadImage.decode().then(() => {
      this.leadImageNode.src = poster;
      spinnerClassList.remove("has-portrait-content", "has-landscape-content");

      if (preloadImage.naturalWidth <= preloadImage.naturalHeight) {
        spinnerClassList.add("has-portrait-content");
      } else {
        spinnerClassList.add("has-landscape-content");
      }

      return this.spinnerNode.className;
    });
  }

  applyViewerMode(overrideViewerMode) {
    let finalViewerMode = overrideViewerMode ?? this.defaultViewerMode;

    if (this.boundingBox && !overrideViewerMode) {
      finalViewerMode = VIEWER_MODE_BOUNDING_BOX;
    }

    this.viewerMode = finalViewerMode;
  }

  renderBoundingBox() {
    const targetSize = this.boundingBoxHandler.getTargetSize();

    this.contentWidth = targetSize.width;
    this.contentHeight = targetSize.height;

    this.zoomHandler.setContentSize(this.contentWidth, this.contentHeight);
    this.zoomHandler.setZoomPoint(null, null, this.zoomScale);

    return targetSize;
  }

  recalculateLayout(callback) {
    const spinnerRect = this.spinnerNode.getBoundingClientRect();
    this.spinnerBoundingRect = spinnerRect;
    this.sceneWidth = spinnerRect.width;
    this.sceneHeight = spinnerRect.height;
    this.uiX = spinnerRect.x;
    this.uiY = spinnerRect.y;
    this.spinnerNode.classList.remove("stretch-content", "short");
    this.leadImageNode.style.cssText = "";
    this.zoomHandler.setSceneSize(this.sceneWidth, this.sceneHeight);

    if (this.sceneHeight < 540) {
      this.spinnerNode.classList.add("short");
    }

    this.canvasLayer.setSize(this.sceneWidth, this.sceneHeight);

    requestAnimationFrame(() => {
      const leadImageRect = this.leadImageNode.getBoundingClientRect();
      if (leadImageRect.height < spinnerRect.height) {
        this.spinnerNode.classList.add("stretch-content");
      }

      requestAnimationFrame(() => {
        this.leadImageBoundingRect = this.leadImageNode.getBoundingClientRect();
        this.aspectRatioCSS = `${this.leadImageNode.naturalWidth}/${this.leadImageNode.naturalHeight}`;
        this.controlsLayer.cssText = `aspect-ratio: ${this.aspectRatioCSS}`;

        this.renderBoundingBox();

        if (callback) {
          callback();
        }
      });
    });
  }
}

export default Spinner;
